/**
 * AI Recruit - Resume Formatter Templates - JSON Tree Node
 * Recursive, drag-source-only rendering of one value inside the Document Map
 * Editor's "Variables" sidebar (see DocumentMapEditor.tsx). Ported from
 * VMP's json-tree-node.component.ts - same recursive shape and drag-payload
 * contract, rebuilt as a plain React component (no framework-specific
 * output-event chain needed: the browser's native HTML5 drag-and-drop
 * dataTransfer already carries the payload straight to the drop handler in
 * DocumentMapEditor, so there is no need to bubble a callback up through
 * every ancestor node the way the Angular version's unused `dragStarted`
 * output did).
 */

import React, { useState } from 'react'

// The actual resume_analysis JSON shown here can be arbitrarily shaped -
// this is intentionally a structural (not domain) type, mirroring the
// backend's own "no fixed field catalog" design (see field_schema.py).
export type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue }

export interface JsonTreeDragPayload {
  kind: 'scalar' | 'array-of-objects' | 'array-of-primitives'
  insertValue: string
  label: string
  // Set only when insertValue is a BARE field name (e.g. "position") that is
  // only meaningful when dropped inside the repeating section whose
  // data-repeat value equals this - lets the drop handler warn if it's
  // dropped somewhere it won't actually render.
  arrayRootPath?: string
}

interface JsonTreeNodeProps {
  keyLabel: string
  value: JsonValue
  absolutePath: string
  // The data-repeat value of the nearest ancestor array this node sits
  // inside (null if this node is not nested inside any array).
  arrayRootPath?: string | null
  depth?: number
  // Defaults open so the panel reads like pretty-printed JSON rather than a
  // collapsed accordion the reviewer has to click through.
  startExpanded?: boolean
}

const isPlainObject = (value: JsonValue): value is { [key: string]: JsonValue } =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

const displayValue = (value: JsonValue): string => {
  if (value === null || value === undefined) return 'null'
  if (typeof value === 'string') return value.length > 60 ? `${value.slice(0, 60)}...` : value
  return String(value)
}

const setDragPayload = (event: React.DragEvent, payload: JsonTreeDragPayload) => {
  event.stopPropagation()
  event.dataTransfer.setData('application/json', JSON.stringify(payload))
  event.dataTransfer.effectAllowed = 'copy'
}

const JsonTreeNode: React.FC<JsonTreeNodeProps> = ({
  keyLabel,
  value,
  absolutePath,
  arrayRootPath = null,
  depth = 0,
  startExpanded = true,
}) => {
  const [expanded, setExpanded] = useState(startExpanded)

  const isArray = Array.isArray(value)
  const isObject = isPlainObject(value)
  const isScalar = !isArray && !isObject

  // A bare name (relative to the current repeat item) when nested inside an
  // array, otherwise the field's real full dotted path from the JSON root.
  const insertValue = arrayRootPath ? keyLabel : absolutePath
  const childArrayRootPath = insertValue
  const childPath = (key: string) => (absolutePath ? `${absolutePath}.${key}` : key)

  const sampleItem: JsonValue | null = isArray && value.length ? value[0] : null
  const sampleItemIsObject = sampleItem !== null && isPlainObject(sampleItem)

  const toggle = () => {
    if (isArray || isObject) setExpanded((e) => !e)
  }

  const onLeafDragStart = (event: React.DragEvent) => {
    setDragPayload(event, {
      kind: 'scalar',
      insertValue,
      label: keyLabel,
      arrayRootPath: arrayRootPath ?? undefined,
    })
  }

  const onArrayDragStart = (event: React.DragEvent) => {
    setDragPayload(event, {
      kind: sampleItemIsObject ? 'array-of-objects' : 'array-of-primitives',
      insertValue,
      label: keyLabel,
      arrayRootPath: arrayRootPath ?? undefined,
    })
  }

  if (isScalar) {
    return (
      <div
        className="flex cursor-grab items-center gap-1.5 rounded py-0.5 hover:bg-gray-50 active:cursor-grabbing"
        style={{ paddingLeft: depth * 12 }}
        draggable
        onDragStart={onLeafDragStart}
        title={`Drag to bind: ${insertValue}`}
      >
        <span className="whitespace-nowrap font-semibold text-indigo-900">{keyLabel}:</span>
        <span className="truncate text-gray-500">{displayValue(value)}</span>
      </div>
    )
  }

  return (
    <div className="text-xs">
      <div className="flex items-center gap-1.5 py-0.5" style={{ paddingLeft: depth * 12 }}>
        <span className="w-3 shrink-0 cursor-pointer select-none text-gray-400" onClick={toggle}>
          {expanded ? '▾' : '▸'}
        </span>
        <span className="cursor-pointer whitespace-nowrap font-semibold text-indigo-900" onClick={toggle}>
          {keyLabel}
        </span>
        {isArray && (
          <span className="rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">array</span>
        )}
        {isArray && (
          <span
            draggable
            onDragStart={onArrayDragStart}
            title="Drag onto the document to insert a new repeating section for this list"
            className="ml-1 cursor-grab select-none whitespace-nowrap rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-900 active:cursor-grabbing"
          >
            + Repeat Section
          </span>
        )}
      </div>

      {expanded && isObject && (
        <div className="ml-[5px] border-l border-dashed border-gray-200">
          {Object.keys(value).map((k) => (
            <JsonTreeNode
              key={k}
              keyLabel={k}
              value={value[k]}
              absolutePath={childPath(k)}
              arrayRootPath={arrayRootPath}
              depth={depth + 1}
            />
          ))}
        </div>
      )}

      {expanded && isArray && sampleItem !== null && (
        <div className="ml-[5px] border-l border-dashed border-gray-200">
          <div className="py-0.5 text-[11px] italic text-gray-500" style={{ paddingLeft: (depth + 1) * 12 }}>
            Example item - drag a field below to bind it inside this repeating section:
          </div>
          {sampleItemIsObject ? (
            <JsonTreeNode
              keyLabel="(each item)"
              value={sampleItem}
              absolutePath={absolutePath}
              arrayRootPath={childArrayRootPath}
              depth={depth + 1}
              startExpanded
            />
          ) : (
            <div
              className="flex cursor-grab items-center gap-1.5 py-0.5 active:cursor-grabbing"
              style={{ paddingLeft: (depth + 1) * 12 }}
              draggable
              onDragStart={onArrayDragStart}
              title="Each item is a plain string value, not an object - drag the array's own '+ Repeat Section' chip above instead"
            >
              <span className="truncate text-gray-500">{displayValue(sampleItem)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default JsonTreeNode
